const ANALYTICS_SESSION_KEY = 'zy:analytics-session-id'

export type AnalyticsEventName =
  | 'page_view'
  | 'contact_form_submit_success'
  | 'contact_form_submit_error'
  | 'questionnaire_view'
  | 'questionnaire_start'
  | 'questionnaire_get_it_now'
  | 'questionnaire_skip'
  | 'questionnaire_complete'
  | 'questionnaire_skip_to_checkout'
  | 'demo_step_view'
  | 'demo_help_submit_success'
  | 'demo_help_submit_error'
  | 'calendly_click'
  | 'pricing_cloud_book_call_click'
  | 'download_gate_view'
  | 'download_gate_submit_success'
  | 'download_gate_submit_error'
  | 'email_capture_submit_success'
  | 'email_capture_submit_error'
  | 'signup_view'
  | 'signup_submit_error'
  | 'exit_capture_shown'
  | 'exit_capture_dismiss'
  | 'exit_capture_submit_success'
  | 'exit_capture_submit_error'
  | 'hero_download_click'
  | 'trial_cta_click'
  | 'pricing_cta_click'
  | 'faq_link_click'
  | 'proof_resources_link_click'
  | 'qualify_view'
  | 'qualify_start'
  | 'qualify_submit'
  | 'qualify_passed'
  | 'qualify_nurture'
  | 'qualify_cta_click'
  | 'private_system_message_sent'

type AnalyticsMetadata = Record<string, string | number | boolean | null | undefined>

const analyticsEnabled = () => import.meta.env.VITE_ANALYTICS_ENABLED === 'true'

const getApiBaseUrl = (): string => {
  const configuredBaseUrl = import.meta.env.VITE_API_URL
  if (configuredBaseUrl) {
    if (typeof window !== 'undefined') {
      const isProdHost = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const configuredIsLocalhost = configuredBaseUrl.includes('localhost') || configuredBaseUrl.includes('127.0.0.1')
      if (isProdHost && configuredIsLocalhost) {
        return window.location.origin
      }
    }

    return configuredBaseUrl
  }

  if (typeof window !== 'undefined') {
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    if (isLocalHost) {
      return 'http://localhost:3001'
    }

    return window.location.origin
  }

  return 'http://localhost:3001'
}

const createSessionId = () => {
  const randomPart = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '')
    : Math.random().toString(36).slice(2) + Date.now().toString(36)

  return `zy_${randomPart}`
}

const getSessionId = () => {
  if (typeof localStorage === 'undefined') return createSessionId()

  try {
    const existing = localStorage.getItem(ANALYTICS_SESSION_KEY)
    if (existing) return existing

    const created = createSessionId()
    localStorage.setItem(ANALYTICS_SESSION_KEY, created)
    return created
  } catch {
    return createSessionId()
  }
}

const currentPage = () => {
  if (typeof window === 'undefined') return '/'
  return `${window.location.pathname}${window.location.search}`
}

export const trackEvent = (eventName: AnalyticsEventName, metadata: AnalyticsMetadata = {}) => {
  if (!analyticsEnabled()) return
  if (typeof fetch === 'undefined') return

  const body = JSON.stringify({
    eventName,
    page: currentPage(),
    sessionId: getSessionId(),
    source: metadata.source,
    metadata,
    timestamp: new Date().toISOString(),
  })

  fetch(`${getApiBaseUrl()}/api/analytics/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: body.length < 60000,
  }).catch(() => {
    // First-party analytics should never block the visitor path.
  })
}

export const trackPageView = (page: string) => {
  trackEvent('page_view', { page })
}
